/**
 * Best-effort screen wake lock so a kiosk iPad doesn't dim/sleep mid-shift. `navigator.wakeLock` is
 * uneven (iPad Safari support is version-dependent) so every call is guarded and failure is a silent
 * no-op — the station just behaves like any page that can dim. The lock is auto-released when the page
 * is hidden, so we re-acquire on `visibilitychange` while the hook is mounted.
 */
import { useEffect } from 'react';

// The Wake Lock API isn't in every TS lib.dom target and isn't on every browser, so reach it through a
// narrow structural type (double cast) and treat absence as a no-op. Kept local to avoid a global aug.
type WakeLockSentinelLike = { release: () => Promise<void> };
type WakeLockLike = { request: (type: 'screen') => Promise<WakeLockSentinelLike> };

export function useWakeLock(enabled = true): void {
  useEffect(() => {
    if (!enabled) return;
    const wl = (navigator as unknown as { wakeLock?: WakeLockLike }).wakeLock;
    if (!wl) return;

    let sentinel: WakeLockSentinelLike | null = null;
    let cancelled = false;

    const acquire = async () => {
      try {
        const held = await wl.request('screen');
        if (cancelled) {
          void held.release().catch(() => {});
          return;
        }
        sentinel = held;
      } catch {
        // denied / unsupported / page not visible — leave it dimmable
      }
    };

    const onVisibility = () => {
      if (document.visibilityState === 'visible' && !cancelled) void acquire();
    };

    void acquire();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisibility);
      void sentinel?.release().catch(() => {});
    };
  }, [enabled]);
}
